import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source'

import type { ChatStreamEvent } from '../types'

export interface ConnectOpts {
  apiBase: string
  jwt: string
  lastEventId: string | undefined
  signal: AbortSignal
  onEvent: (event: ChatStreamEvent) => void
  onOpen: (response: Response) => void
  /**
   * Llegó algo por el stream (evento `chat` o latido `heartbeat`): está vivo de verdad.
   * Abrir no basta — con Redis caído el servidor responde 200 y el stream muere al momento.
   */
  onAlive: () => void
  onError: (err: unknown) => void
}

const BACKOFF_BASE_MS = 1_000
const BACKOFF_CAP_MS = 30_000

/**
 * Abre la conexión SSE al endpoint /me/chat-stream y reenvía eventos
 * `chat` parseados al callback. Los `heartbeat` solo avisan de que el stream vive (`onAlive`).
 * El caller controla el ciclo de vida via `signal` (AbortController).
 *
 * Backoff: 1s, 2s, 4s, 8s, 16s, 30s (cap). Resetea a 0 cuando llega algo por el stream,
 * NO al abrir: un stream que abre y muere al instante (Redis caído) sigue acumulando.
 */
export function connectChatStream(opts: ConnectOpts): void {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${opts.jwt}`,
    Accept: 'text/event-stream',
  }
  if (opts.lastEventId) headers['Last-Event-ID'] = opts.lastEventId

  let retryAttempt = 0

  // apiBase ya incluye /api/v1 (igual que VITE_API_BASE_URL del httpClient).
  void fetchEventSource(`${opts.apiBase}/me/chat-stream`, {
    headers,
    signal: opts.signal,
    openWhenHidden: true,
    async onopen(response) {
      if (response.ok && response.headers.get('Content-Type')?.includes(EventStreamContentType)) {
        opts.onOpen(response)
        return
      }
      if (response.status === 401 || response.status === 403) {
        throw new FatalAuthError(response.status)
      }
      throw new Error(`SSE open failed: ${response.status}`)
    },
    onmessage(msg) {
      retryAttempt = 0
      opts.onAlive()
      if (msg.event !== 'chat') return
      try {
        const parsed = JSON.parse(msg.data) as ChatStreamEvent
        opts.onEvent(parsed)
      } catch (e) {
        opts.onError(e)
      }
    },
    // Si el servidor cierra el stream (timeout de 30 min, o Redis caído), fetchEventSource
    // termina SIN reintentar salvo que aquí se lance un error: lanzarlo lo lleva a `onerror`
    // (reintento con backoff, cuenta para el sondeo de respaldo). Sin esto, el tiempo real se
    // moría en silencio a la media hora y el chat no volvía a recibir nada.
    onclose() {
      throw new StreamClosedError()
    },
    onerror(err) {
      opts.onError(err)
      // Lanzar es la única forma de que fetchEventSource deje de reintentar: auth fatal, o el
      // caller abortó (pasó a sondeo y reintentará él más tarde). Abortar sin más no basta:
      // la librería programa el siguiente intento justo después de este callback.
      if (err instanceof FatalAuthError || opts.signal.aborted) throw err
      // Backoff exponencial: 1s, 2s, 4s, 8s, 16s, 30s. Return = ms hasta reintentar.
      const delay = Math.min(BACKOFF_BASE_MS * 2 ** retryAttempt, BACKOFF_CAP_MS)
      retryAttempt += 1
      return delay
    },
  })
}

/** El servidor cerró el stream: se reconecta como cualquier otro fallo. */
export class StreamClosedError extends Error {
  constructor() {
    super('SSE stream closed by server')
  }
}

export class FatalAuthError extends Error {
  constructor(public readonly status: number) {
    super(`Auth failed: ${status}`)
  }
}
